import axios from "axios";
import { supabase } from "../supaBaseClient.ts";
import type {
  FootballDataResponse,
  League,
  Standing,
  Tip,
} from "../types/index.ts";
import type { Inserts } from "../supaBaseClient.ts";
import { type ApiResponse, errorHandler } from "./ErrorHandlingService.ts";
import { sanitizeSeason } from "../utils/footballDataSeason.ts";
import { authedHeaders } from "../utils/authHeaders.ts";

export class LeagueService {
  private static instance: LeagueService;

  private constructor() {}

  public static getInstance(): LeagueService {
    if (!LeagueService.instance) {
      LeagueService.instance = new LeagueService();
    }
    return LeagueService.instance;
  }

  /**
   * Fetch all available leagues from the database (new error handling with REST API)
   */
  async fetchLeaguesWithErrorHandling(): Promise<ApiResponse<League[]>> {
    try {
      const response = await fetch(
        "https://fvuwfonfrllbjqvywula.supabase.co/rest/v1/leagues?select=*&is_active=eq.true&order=display_order",
        {
          method: "GET",
          headers: {
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            "Content-Type": "application/json",
          },
        },
      );

      const result = await errorHandler.handleHttpResponse<League[]>(response, {
        operation: "fetchLeagues",
      });

      if (!result.success) {
        return errorHandler.createErrorResponse(result.error!);
      }

      // Wenn keine Leagues gefunden wurden, initialisiere Standard-Leagues
      if (!result.data || result.data.length === 0) {
        await this.initializeDefaultLeagues();

        // Try again after initialization
        const retryResponse = await fetch(
          "https://fvuwfonfrllbjqvywula.supabase.co/rest/v1/leagues?select=*&is_active=eq.true&order=display_order",
          {
            method: "GET",
            headers: {
              apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
              Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              "Content-Type": "application/json",
            },
          },
        );

        const retryResult = await errorHandler.handleHttpResponse<League[]>(
          retryResponse,
          {
            operation: "fetchLeagues_retry",
          },
        );

        if (!retryResult.success) {
          return errorHandler.createErrorResponse(retryResult.error!);
        }

        return errorHandler.createSuccess(retryResult.data || []);
      }

      return errorHandler.createSuccess(result.data);
    } catch (error) {
      errorHandler.handleJSError(error, "Failed to fetch leagues", {
        operation: "fetchLeagues",
      });

      // Fallback to default leagues even on JS errors
      return errorHandler.createSuccess(this.getDefaultLeagues());
    }
  }

  /**
   * Fetch all available leagues from the database (legacy method for compatibility)
   */
  async fetchLeagues(): Promise<League[]> {
    const result = await this.fetchLeaguesWithErrorHandling();
    return result.data || [];
  }

  /**
   * Fetch coming-soon leagues (is_active=false, coming_soon=true) for display
   * as teasers in the competition picker.
   */
  async fetchComingSoonLeagues(): Promise<League[]> {
    try {
      const res = await fetch(
        "https://fvuwfonfrllbjqvywula.supabase.co/rest/v1/leagues?select=*&is_active=eq.false&coming_soon=eq.true&order=display_order",
        {
          method: "GET",
          headers: {
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!res.ok) return [];
      return (await res.json()) as League[];
    } catch {
      return [];
    }
  }

  /**
   * All competitions for the picker UI: active + coming-soon, ordered by
   * display_order. Active leagues come first (they have lower display_order).
   */
  async fetchAllForPicker(): Promise<League[]> {
    const [active, soon] = await Promise.all([
      this.fetchLeagues(),
      this.fetchComingSoonLeagues(),
    ]);
    return [...active, ...soon];
  }

  /**
   * Fetch standings for a specific league from the API
   */
  async fetchStandings(leagueCode: string): Promise<Standing[]> {
    try {
      // Get league configuration from database using REST API
      const leagueResponse = await fetch(
        `https://fvuwfonfrllbjqvywula.supabase.co/rest/v1/leagues?select=*&code=eq.${leagueCode}`,
        {
          method: "GET",
          headers: {
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!leagueResponse.ok) {
        console.error("Failed to fetch league config:", leagueResponse.status);
        return [];
      }

      const leagues = await leagueResponse.json();
      const league = leagues[0];

      if (!league) {
        console.error("League not found:", leagueCode);
        return [];
      }

      // Route to appropriate API based on league code
      console.log(
        `Fetching standings for league: ${leagueCode}, endpoint: ${league.api_endpoint}`,
      );
      // football-data.org competitions (proxied via backend)
      let standings: Standing[] = [];
      if (leagueCode === "pl" || leagueCode === "cl") {
        const competitionCode = leagueCode === "pl" ? "PL" : "CL";
        standings = await this.fetchFootballDataStandingsByCode(
          competitionCode,
          league.season,
        );
      } else {
        standings = await this.fetchOpenLigaDBStandings(league.api_endpoint);
      }

      // Fallback: wenn die echte API keine Daten hat (Saison noch nicht gestartet)
      // und mock_standings in der DB vorhanden sind, diese verwenden.
      if (standings.length === 0 && Array.isArray(league.mock_standings) && league.mock_standings.length > 0) {
        console.log(`[mock] Nutze mock_standings für ${leagueCode} (${league.mock_standings.length} Teams)`);
        return league.mock_standings as Standing[];
      }

      return standings;
    } catch (error) {
      console.error(
        `Failed to fetch standings for league ${leagueCode}:`,
        error,
      );
      return [];
    }
  }

  private async fetchOpenLigaDBStandings(
    endpoint: string,
  ): Promise<Standing[]> {
    try {
      console.log("Fetching from OpenLigaDB:", endpoint);
      const response = await axios.get(endpoint);
      console.log("OpenLigaDB response:", response.data);

      if (!Array.isArray(response.data)) {
        console.error("OpenLigaDB response is not an array:", response.data);
        return [];
      }

      // Transform OpenLigaDB API data to match our types (camelCase to PascalCase)
      const transformedData = response.data.map(
        (team: Record<string, unknown>) => ({
          TeamId: team.teamInfoId as number,
          TeamName: team.teamName as string,
          ShortName: team.shortName as string,
          TeamIconUrl: team.teamIconUrl as string,
          Points: team.points as number,
          OpponentGoals: team.opponentGoals as number,
          Goals: team.goals as number,
          Matches: team.matches as number,
          Won: team.won as number,
          Lost: team.lost as number,
          Draw: team.draw as number,
          GoalDiff: team.goalDiff as number,
        }),
      );

      console.log("Transformed OpenLigaDB data:", transformedData);
      return transformedData;
    } catch (error) {
      console.error("Error fetching OpenLigaDB standings:", error);
      return [];
    }
  }

  /**
   * Generic football-data.org standings fetcher via the backend proxy.
   * Supports any competition code in the server's ALLOWED_COMPETITION_CODES set
   * (currently PL and CL). The proxy endpoint is /api/standings/:code.
   *
   * The season comes from the `leagues` row and is passed on, because
   * football-data.org otherwise keeps serving the previous season's final
   * table. Invalid values are dropped rather than sent along.
   */
  private async fetchFootballDataStandingsByCode(
    competitionCode: string,
    season?: unknown,
  ): Promise<Standing[]> {
    try {
      // Relative on purpose: production serves /api from server.js, the Vite
      // dev server proxies it to `npm run proxy` (see vite.config.ts).
      const validSeason = sanitizeSeason(season);
      const apiUrl = `/api/standings/${competitionCode}` +
        (validSeason ? `?season=${validSeason}` : "");

      console.log(`Fetching football-data.org standings for ${competitionCode} from:`, apiUrl);
      const response = await axios.get(apiUrl);

      console.log(`Football-data.org ${competitionCode} response:`, response.data);
      const data: FootballDataResponse = response.data;

      if (!data.standings || !data.standings[0] || !data.standings[0].table) {
        console.error("Invalid football-data.org response structure:", data);
        return [];
      }

      // Transform to our PascalCase Standing type (same shape for PL and CL)
      const transformedData = data.standings[0].table.map((team) => ({
        TeamId: team.team.id,
        TeamName: team.team.name,
        ShortName: team.team.shortName,
        TeamIconUrl: team.team.crest,
        Points: team.points,
        OpponentGoals: team.goalsAgainst,
        Goals: team.goalsFor,
        Matches: team.playedGames,
        Won: team.won,
        Lost: team.lost,
        Draw: team.draw,
        GoalDiff: team.goalDifference,
      }));

      console.log(`Transformed ${competitionCode} data:`, transformedData);
      return transformedData;
    } catch (error) {
      console.error(`Error fetching football-data.org standings for ${competitionCode}:`, error);
      return [];
    }
  }


  /**
   * Get user tips for a specific league (new error handling)
   */
  async getUserTipsForLeagueWithErrorHandling(
    userId: string,
    leagueId: number,
    tipRoundId: number,
  ): Promise<ApiResponse<Tip | null>> {
    try {
      const response = await fetch(
        `https://fvuwfonfrllbjqvywula.supabase.co/rest/v1/tips_v2?user_id=eq.${userId}&league_id=eq.${leagueId}&tip_round_id=eq.${tipRoundId}`,
        {
          method: "GET",
          // tips_v2 is readable for authenticated users only (Migration 018,
          // "Anon stays blocked") — with the anon key this silently returns [].
          headers: authedHeaders(import.meta.env.VITE_SUPABASE_ANON_KEY),
        },
      );

      const result = await errorHandler.handleHttpResponse<Tip[]>(response, {
        operation: "getUserTips",
        userId,
        leagueId,
      });

      if (!result.success) {
        return errorHandler.createErrorResponse(result.error!);
      }

      // Return first tip if found, null if empty (normal case)
      const tip = result.data && result.data.length > 0 ? result.data[0] : null;
      return errorHandler.createSuccess(tip);
    } catch (error) {
      const appError = errorHandler.handleJSError(
        error,
        "Failed to fetch user tips",
        { operation: "getUserTips", userId, leagueId },
      );
      return errorHandler.createErrorResponse(appError);
    }
  }

  /**
   * Get user tips for a specific league within a tip round.
   */
  async getUserTipsForLeague(
    userId: string,
    leagueId: number,
    tipRoundId: number,
  ): Promise<Tip | null> {
    const result = await this.getUserTipsForLeagueWithErrorHandling(
      userId,
      leagueId,
      tipRoundId,
    );
    return result.data || null;
  }

  /**
   * Save user tips for a specific league within a tip round.
   * Uniqueness key is (user_id, tip_round_id, league_id) — see migration 011.
   */
  async saveUserTipsForLeague(
    userId: string,
    leagueId: number,
    tipRoundId: number,
    teams: Standing[],
  ): Promise<Tip | null> {
    try {
      const tipV2Data = {
        user_id: userId,
        league_id: leagueId,
        tip_round_id: tipRoundId,
        teams: teams,
      };

      // PostgREST upsert on the (user, round, league) unique constraint.
      // Must carry the user's token: tips_v2 has RLS since Migration 018, and
      // the anon key leaves auth.uid() NULL, which every policy rejects.
      const response = await fetch(
        `https://fvuwfonfrllbjqvywula.supabase.co/rest/v1/tips_v2?on_conflict=user_id,tip_round_id,league_id`,
        {
          method: "POST",
          headers: {
            ...authedHeaders(import.meta.env.VITE_SUPABASE_ANON_KEY),
            Prefer: "resolution=merge-duplicates,return=minimal",
          },
          body: JSON.stringify(tipV2Data),
        },
      );

      if (response.ok) {
        return tipV2Data as unknown as Tip;
      }
      const errorText = await response.text();
      console.error("Failed to save tips:", response.status, errorText);
      return null;
    } catch (error) {
      console.error("Failed to save tips:", error);
      return null;
    }
  }

  /**
   * Get all tips for a specific league within a tip round.
   * Filtered by tip_round_id so each round's leaderboard is independent.
   */
  async getAllTipsForLeague(
    leagueId: number,
    tipRoundId: number,
  ): Promise<Tip[]> {
    try {
      return await this.getAllTipsWithProfilesFallback(leagueId, tipRoundId);
    } catch (error) {
      console.error("Failed to fetch all tips:", error);
      return [];
    }
  }

  /**
   * Fallback method to get tips and profiles separately
   */
  private async getAllTipsWithProfilesFallback(
    leagueId: number,
    tipRoundId: number,
  ): Promise<Tip[]> {
    try {
      // Get tips (filtered by tip round)
      const tipsResponse = await fetch(
        `https://fvuwfonfrllbjqvywula.supabase.co/rest/v1/tips_v2?select=*&league_id=eq.${leagueId}&tip_round_id=eq.${tipRoundId}`,
        {
          method: "GET",
          // Same as above: the anon key yields an empty list, which is why the
          // leaderboard showed "no tips yet" while rows existed.
          headers: authedHeaders(import.meta.env.VITE_SUPABASE_ANON_KEY),
        },
      );

      if (!tipsResponse.ok) {
        console.error("Failed to fetch tips:", tipsResponse.status);
        return [];
      }

      const tips = await tipsResponse.json();

      if (tips.length === 0) {
        return tips;
      }

      // Get all user IDs from tips
      const userIds = [...new Set(tips.map((tip: Tip) => tip.user_id))];

      // Get profiles for all users
      const profilesResponse = await fetch(
        `https://fvuwfonfrllbjqvywula.supabase.co/rest/v1/profiles?select=user_id,display_name&user_id=in.(${
          userIds
            .map((id) => `"${id}"`)
            .join(",")
        })`,
        {
          method: "GET",
          headers: {
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (profilesResponse.ok) {
        const profiles = await profilesResponse.json();
        const profileMap = new Map(
          profiles.map((p: { user_id: string; display_name: string }) => [
            p.user_id,
            p.display_name,
          ]),
        );

        // Add display names to tips
        tips.forEach((tip: Tip & { profiles?: { display_name: string } }) => {
          const displayName = profileMap.get(tip.user_id) as string | undefined;
          tip.profiles = {
            display_name: displayName || tip.user_id,
          };
        });
      } else {
        console.error("Failed to fetch profiles:", profilesResponse.status);
      }

      return tips;
    } catch (error) {
      console.error("Fallback method failed:", error);
      return [];
    }
  }

  /**
   * Initialize default leagues if they don't exist
   */
  async initializeDefaultLeagues(): Promise<void> {
    try {
      const defaultLeagues: Inserts<"leagues">[] = [
        {
          name: "1. Bundesliga",
          code: "bl1",
          api_endpoint: "https://api.openligadb.de/getbltable/bl1/2025",
          country: "Deutschland",
          season: "2025",
          is_active: true,
          display_order: 1,
        },
        {
          name: "2. Bundesliga",
          code: "bl2",
          api_endpoint: "https://api.openligadb.de/getbltable/bl2/2025",
          country: "Deutschland",
          season: "2025",
          is_active: true,
          display_order: 2,
        },
      ];

      for (const league of defaultLeagues) {
        const { error } = await supabase.from("leagues").upsert(league, {
          onConflict: "code",
        });

        if (error) {
          console.error(`Error initializing league ${league.code}:`, error);
        }
      }
    } catch (error) {
      console.error("Failed to initialize default leagues:", error);
    }
  }

  /**
   * Get default leagues as fallback
   */
  getDefaultLeagues(): League[] {
    return [
      {
        id: 1,
        name: "1. Bundesliga",
        code: "bl1",
        api_endpoint: "https://api.openligadb.de/getbltable/bl1/2025",
        country: "Deutschland",
        season: "2025",
        is_active: true,
        coming_soon: false,
        display_order: 1,
        type: "LEAGUE",
        season_label: "25/26",
        start_date: "2025-08-22",
        lock_date: "2025-08-22",
        logo_url: null,
        mock_standings: null,
      },
      {
        id: 2,
        name: "2. Bundesliga",
        code: "bl2",
        api_endpoint: "https://api.openligadb.de/getbltable/bl2/2025",
        country: "Deutschland",
        season: "2025",
        is_active: true,
        coming_soon: false,
        display_order: 2,
        type: "LEAGUE",
        season_label: "25/26",
        start_date: "2025-08-08",
        lock_date: "2025-08-08",
        logo_url: null,
        mock_standings: null,
      },
    ];
  }

  /**
   * Get mock standings data as fallback
   */
  getMockStandings(): Standing[] {
    return [
      {
        TeamId: 1,
        TeamName: "FC Bayern München",
        ShortName: "Bayern",
        TeamIconUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/1/1b/FC_Bayern_M%C3%BCnchen_logo_%282017%29.svg/240px-FC_Bayern_M%C3%BCnchen_logo_%282017%29.svg.png",
        Points: 45,
        OpponentGoals: 15,
        Goals: 55,
        Matches: 20,
        Won: 14,
        Lost: 2,
        Draw: 4,
        GoalDiff: 40,
      },
      {
        TeamId: 2,
        TeamName: "Borussia Dortmund",
        ShortName: "Dortmund",
        TeamIconUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/6/67/Borussia_Dortmund_logo.svg/240px-Borussia_Dortmund_logo.svg.png",
        Points: 38,
        OpponentGoals: 25,
        Goals: 48,
        Matches: 20,
        Won: 12,
        Lost: 4,
        Draw: 4,
        GoalDiff: 23,
      },
      {
        TeamId: 3,
        TeamName: "RB Leipzig",
        ShortName: "Leipzig",
        TeamIconUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/0/04/RB_Leipzig_2014_logo.svg/240px-RB_Leipzig_2014_logo.svg.png",
        Points: 35,
        OpponentGoals: 22,
        Goals: 42,
        Matches: 20,
        Won: 11,
        Lost: 4,
        Draw: 5,
        GoalDiff: 20,
      },
      {
        TeamId: 4,
        TeamName: "Bayer Leverkusen",
        ShortName: "Leverkusen",
        TeamIconUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/f/f7/Bayer_Leverkusen_Logo.svg/240px-Bayer_Leverkusen_Logo.svg.png",
        Points: 32,
        OpponentGoals: 28,
        Goals: 38,
        Matches: 20,
        Won: 10,
        Lost: 5,
        Draw: 5,
        GoalDiff: 10,
      },
    ];
  }
}

export const leagueService = LeagueService.getInstance();

// Test-Methode für Supabase-Verbindung
export async function testSupabaseConnection(): Promise<boolean> {
  try {
    const { error } = await supabase.from("leagues").select("id").limit(1);

    if (error) {
      console.error("Supabase connection test failed:", error);
      return false;
    }
    return true;
  } catch (error) {
    console.error("Supabase connection test failed:", error);
    return false;
  }
}
